import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import { useNavigate } from "react-router-dom";
import CustomerTable from "./CustomerPageTable";
import { getCustomer } from "redux/actions/customerMaster";
import { AppState } from "redux/store";
import { isModuleAuthorized } from "@crema/utility/Utils";

const Customer = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { customer } = useSelector<AppState, AppState["customerMaster"]>(
    ({ customerMaster }) => customerMaster
  );

  const rowsPerPage = 10;

  const [search, setSearchQuery] = useState<string>("");
  const [searchField, setSearchField] = useState<
    "all" | "contact_name" | "contact_number" | "contact_email"
  >("all");
  const [page, setPage] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string>("");

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Customer Name", value: "contact_name" },
    { label: "Mobile Number", value: "contact_number" },
    { label: "Email ID", value: "contact_email" },
  ];

  useEffect(() => {
    dispatch(getCustomer("", 0, 10000));
  }, [dispatch]);

  const handleAddcustomerClick = () => {
    navigate("/customerCare/CustomerPageCreate");
  };

  const onSearchCustomer = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  const filteredCustomer = customer.filter((item) => {
    const term = search.toLowerCase();

    const matchesSearch = search.trim()
      ? searchField === "all"
        ? item.contact_name?.toLowerCase().includes(term) ||
        item.contact_number?.toLowerCase().includes(term) ||
        item.contact_email?.toLowerCase().includes(term)
        : (item[searchField as keyof typeof item] as string)
          ?.toLowerCase()
          .includes(term)
      : true;

    const matchesDate = selectedDate
      ? item.created_date?.slice(0, 10) === selectedDate
      : true;
    return matchesSearch && matchesDate;
  });

  const paginatedCustomer = filteredCustomer.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );
const exportToCSV = (data: any[]) => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }
  const selectedFields = [
    "id",
    "contact_name",
    "contact_number",
    "contact_email",
    "state",
    "city",
    "area",
    "address",
    "reference",
    "remarks_at_the_time_of_generation",
    "lead_source",
    "created_by",
    "created_date",
  ];
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr; 
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };
  const headers = selectedFields;

  const csvContent =
    [
      headers.join(","), 
      ...data.map((row) =>
        headers.map((key) => {
          let value = row[key] ?? "";
          if (key === "created_date") {
            value = formatDate(value);
          }
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(",")
      ),
    ].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "customer_data.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.customerCare"]}
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              {isModuleAuthorized("Add CC Lead") && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAddcustomerClick}
                  sx={{ flexShrink: 0 }}
                >
                  Add CC Lead
                </Button>
              )}

              <Button
                variant="contained"
                color="secondary"
                onClick={() => exportToCSV(filteredCustomer)}
              >
                Export
              </Button>
            </Box>
          </Box>
        }
        fullView
      >
        {isModuleAuthorized("View CC Lead") && (
          <>
            <AppsHeader>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: 1,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  select
                  size="small"
                  value={searchField}
                  onChange={(e) => {
                    setSearchField(
                      e.target.value as
                      | "all"
                      | "contact_name"
                      | "contact_number"
                      | "contact_email"
                    );
                    setSearchQuery("");
                    setPage(0);
                  }}
                  sx={{ width: 160 }}
                >
                  {searchFields.map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      {field.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={(e) => onSearchCustomer(e.target.value)}
                  placeholder={messages["common.searchHere"] as string}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {search ? (
                          <IconButton
                            onClick={() => onSearchCustomer("")}
                            size="small"
                          >
                            <CloseIcon />
                          </IconButton>
                        ) : (
                          <SearchIcon />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 200 }}
                />

                <TextField
                  label="Filter by Date"
                  type="date"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setPage(0);
                  }}
                  sx={{ width: 180 }}
                />
              </Box>
            </AppsHeader>

            <AppsContent
              sx={{
                paddingTop: 2.5,
                paddingBottom: 2.5,
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
              }}
            >
              <CustomerTable customer={paginatedCustomer} />
              <AppsPagination
                rowsPerPage={rowsPerPage}
                count={filteredCustomer.length}
                page={page}
                onPageChange={onPageChange}
              />
            </AppsContent>
          </>
        )}
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Customer;
